import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Eye, EyeOff, Mail, Lock, Chrome, User, AtSign } from "lucide-react";
import { debug, info } from "../../utils/logger";

const FormContainer = styled.div`
  max-width: 90%;
  width: 100%;
  margin: 0 auto;
  padding: 1.2rem;
  background: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-color);
  border: 1px solid var(--border-color);

  @media (min-width: 480px) {
    max-width: 400px;
    padding: 1.5rem;
  }
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--text-color);
  font-size: 1.5rem;

  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--surface-color);
  color: var(--text-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.1);
  }

  &::placeholder {
    color: var(--text-secondary-color);
  }

  @media (max-width: 479px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary-color);
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-secondary-color);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;

  &:hover {
    color: var(--text-color);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--primary-color);
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(77, 171, 247, 0.3);
  }

  &:disabled {
    background: var(--secondary-color);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background-color: var(--error-bg);
  color: var(--error-text);
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid var(--error-color);
  font-size: 0.875rem;
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    background: #3367d6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
  }

  &:disabled {
    background: var(--secondary-color);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: var(--text-secondary-color);
  font-size: 14px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid var(--border-color);
  }

  span {
    padding: 0 16px;
    background: var(--surface-color);
  }
`;

interface SignupFormProps {
  onSwitchToLogin?: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const { signUp, signInWithGoogle, error, signupSuccess, resetSignupSuccess } =
    useAuth();
  const navigate = useNavigate();

  // Check for admin access - only show email/password if admin mode is enabled
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminKey = urlParams.get("admin");
    const isAdminMode = adminKey === process.env.REACT_APP_ADMIN_SECRET;

    if (isAdminMode) {
      setShowEmailPassword(true);
      debug("Admin mode enabled for signup", {
        adminKey: adminKey?.substring(0, 8) + "...",
      });
    }
  }, []);

  // Redirect on successful signup
  useEffect(() => {
    if (signupSuccess) {
      // Redirect to dashboard
      navigate("/dashboard");
    }
  }, [signupSuccess, navigate]);

  // Reset signup success state when component unmounts
  useEffect(() => {
    return () => {
      resetSignupSuccess();
    };
  }, [resetSignupSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !displayName || !username) return;
    if (password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      info("Attempting account creation", { email, username, displayName });
      await signUp(email, password, displayName, username);
      // If successful, the user will be automatically signed in and redirected
    } catch (err: any) {
      console.error("Account creation failed:", err);
      // Error is already handled in AuthContext, just log it here
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <Title>Get Started</Title>

      {/* Google Sign Up Button - Primary */}
      <GoogleButton type="button" onClick={signInWithGoogle} disabled={loading}>
        <Chrome size={20} />
        {loading ? "Signing In..." : "Continue with Google"}
      </GoogleButton>

      {/* Email/Password Form - Completely hidden from end users */}
      {showEmailPassword && (
        <>
          {/* Divider for better spacing */}
          <Divider>
            <span>or</span>
          </Divider>

          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Icon>
                <User size={20} />
              </Icon>
              <Input
                type="text"
                placeholder="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <Icon>
                <AtSign size={20} />
              </Icon>
              <Input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <Icon>
                <Mail size={20} />
              </Icon>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <Icon>
                <Lock size={20} />
              </Icon>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </InputGroup>

            <InputGroup>
              <Icon>
                <Lock size={20} />
              </Icon>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </InputGroup>

            <Button type="submit" disabled={loading}>
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </Form>
        </>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {signupSuccess && (
        <div
          style={{
            color: "var(--success-color)",
            textAlign: "center",
            marginTop: "1rem",
          }}
        >
          Account created successfully! You can now access the dashboard.
        </div>
      )}

      {/* Removed login link since Google auth handles both signin and signup */}
    </FormContainer>
  );
};

export default SignupForm;
